import { useEffect, useRef, useState, type RefObject } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  getMapboxGestureOptions,
  getMapboxPublicToken,
  getMapboxStyleUrl,
} from "@/lib/mapboxConfig";

export interface MapCamera {
  center: [number, number];
  zoom: number;
}

interface UseEmbeddedMapOptions {
  container: HTMLDivElement | null;
  /** Owned by the parent so the view survives the inline ↔ expanded remount. */
  cameraRef: RefObject<MapCamera | null>;
  /** Where to look when no camera has been persisted yet. */
  initialCamera: MapCamera;
  /** True for inline maps inside a scrolling page (see getMapboxGestureOptions). */
  cooperativeGestures: boolean;
}

/**
 * Creates a Mapbox map on `container` and keeps its canvas sized to it.
 * Returns null until the map exists (or when no Mapbox token is configured).
 */
export function useEmbeddedMap({
  container,
  cameraRef,
  initialCamera,
  cooperativeGestures,
}: UseEmbeddedMapOptions): mapboxgl.Map | null {
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const initialCameraRef = useRef(initialCamera);
  initialCameraRef.current = initialCamera;

  useEffect(() => {
    const token = getMapboxPublicToken();
    if (!container || !token) return;

    mapboxgl.accessToken = token;
    const camera = cameraRef.current ?? initialCameraRef.current;
    const instance = new mapboxgl.Map({
      container,
      style: getMapboxStyleUrl(),
      ...getMapboxGestureOptions(cooperativeGestures),
      center: camera.center,
      zoom: camera.zoom,
    });
    instance.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      "top-right",
    );
    instance.on("moveend", () => {
      const center = instance.getCenter();
      cameraRef.current = {
        center: [center.lng, center.lat],
        zoom: instance.getZoom(),
      };
    });

    // A container that is hidden at creation (inactive tab, opening modal)
    // gives Mapbox a fallback canvas size: follow the real one from then on.
    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(container);
    setMap(instance);

    return () => {
      observer.disconnect();
      instance.remove();
      setMap(null);
    };
  }, [container, cameraRef, cooperativeGestures]);

  return map;
}
